import * as React from 'react';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { __t, applyTranslations } from 'utils/language';
import { getPaymentDetailsCountryType, shouldInfluencerFillBusinessDetails } from 'utils/organization';

import { withModal } from 'components/modal/ModalHOC';
import EditShippingModal, {
  MODE_EDIT_ADDRESS,
  MODE_EDIT_PHONE
} from 'modules/shipping/components/edit-shipping-modal/EditShippingModal';

import './offer-delivery-details.scss';

import type { OrganizationSettingsForm, ShippingAddress } from 'types';

applyTranslations(require('./offer-delivery-details.i18n.json'));

type Props = {
  isForBrand: boolean,
  subcampaignId: number,
  includesDelivery: boolean,
  shippingAddress: ShippingAddress | undefined | null,
  organizationSettings: OrganizationSettingsForm
};

const IconWithModal = withModal((
  <span className="offer-delivery-details__icon">
    <SVGDynamicIcon
      name="pencil-custom-bg"
      clickable
      customColor="grey"
      width={13}
      height={13}
    />
  </span>
), {
  modalContent: EditShippingModal
});

const OfferDeliveryDetails = (
  {
    organizationSettings,
    shippingAddress,
    includesDelivery,
    subcampaignId,
    isForBrand
  }: Props
): JSX.Element => {
  const hasBusinessDetails = organizationSettings && !shouldInfluencerFillBusinessDetails(organizationSettings);
  const { bankDetails, businessDetails, billingAddress } = organizationSettings || {};
  const countryType = getPaymentDetailsCountryType(
    billingAddress?.country?.toLowerCase().trim()
  );

  const renderAddress = () => (
    <div className="offer-delivery-details__item">
      <div className="offer-delivery-details__item-content">
        <div className="offer-delivery-details__header">
          {__t('offer_delivery_details.address_label')}
          {
            !isForBrand && (
              <IconWithModal
                modalContentProps={{
                  subcampaignId,
                  shippingAddress,
                  mode: MODE_EDIT_ADDRESS,
                  isEditCampaignAddress: true
                }}
              />
            )
          }
        </div>
        {
          shippingAddress && (
            <React.Fragment>
              {
                shippingAddress.recipient && (
                  <div>{shippingAddress.recipient}</div>
                )
              }
              {
                shippingAddress.address1 && (
                  <div>{shippingAddress.address1}</div>
                )
              }
              {
                shippingAddress.address2 && (
                  <div>{shippingAddress.address2}</div>
                )
              }
              {
                (shippingAddress.city || shippingAddress.zip) && (
                  <div>
                    {shippingAddress.zip}
                    &nbsp;
                    {shippingAddress.city}
                  </div>
                )
              }
              {
                shippingAddress.stateProvinceRegion && (
                  <div>{shippingAddress.stateProvinceRegion}</div>
                )
              }
              {
                shippingAddress.country && (
                  <div>{shippingAddress.country}</div>
                )
              }
              {
                shippingAddress.email && (
                  <div className="offer-delivery-details__email">
                    {shippingAddress.email}
                  </div>
                )
              }
            </React.Fragment>
          )
        }
      </div>
    </div>
  );

  const renderPhone = () => (
    <div className="offer-delivery-details__item">
      <div className="offer-delivery-details__item-content">
        <div className="offer-delivery-details__header">
          {__t('offer_delivery_details.phone_label')}
          {
            !isForBrand && (
              <IconWithModal
                modalContentProps={{
                  subcampaignId,
                  shippingAddress,
                  mode: MODE_EDIT_PHONE
                }}
              />
            )
          }
        </div>
        <div>
          {shippingAddress?.phone || '–'}
        </div>
      </div>
    </div>
  );

  const renderBankDetails = () => (
    <div className="offer-delivery-details__item">
      <div className="offer-delivery-details__item-content">
        <div className="offer-delivery-details__header">
          {__t('offer_delivery_details.bank_details_label')}
        </div>
        {
          bankDetails?.accountNumber && (
            <div>
              {__t('offer_delivery_details.account_number_label')}
              &nbsp;
              {bankDetails.accountNumber}
            </div>
          )
        }
        {
          bankDetails?.swift && (
            <div>
              {__t('offer_delivery_details.swift_label')}
              &nbsp;
              {bankDetails.swift}
            </div>
          )
        }
      </div>
    </div>
  );

  const renderBusinessDetails = () => (
    <div className="offer-delivery-details__item">
      <div className="offer-delivery-details__item-content">
        <div className="offer-delivery-details__header">
          {__t('offer_delivery_details.business_details_label')}
        </div>
        {
          businessDetails.companyName && (
            <div>{businessDetails.companyName}</div>
          )
        }
        {
          billingAddress.addressLine1 && (
            <div>{billingAddress.addressLine1}</div>
          )
        }
        {
          (billingAddress.city || billingAddress.postalCode) && (
            <div>
              {billingAddress.postalCode}
              &nbsp;
              {billingAddress.city}
            </div>
          )
        }
        {
          billingAddress.state && (
            <div>{billingAddress.state}</div>
          )
        }
        {
          billingAddress.country && (
            <div>{billingAddress.country}</div>
          )
        }
        {
          countryType === 'germany' && (
            <div>
              {__t('offer_delivery_details.entrepreneur_label')}
              &nbsp;
              {String(businessDetails.entrepreneur) === 'true' ? 'yes' : 'no'}
            </div>
          )
        }
        {
          businessDetails.taxId && (
            <div>
              {__t('offer_delivery_details.tax_label')}
              &nbsp;
              {businessDetails.taxId}
            </div>
          )
        }
      </div>
    </div>
  );

  return (
    <div className="offer-delivery-details__wrapper">
      <div className="offer-delivery-details">
        {renderPhone()}
        {includesDelivery && shippingAddress && renderAddress()}
        {hasBusinessDetails && renderBusinessDetails()}
        {renderBankDetails()}
      </div>
    </div>
  );
};

export default OfferDeliveryDetails;
